import { BaseUIService } from '@/modules/core/ui/services/BaseUIService';
import { PlatformProduct } from '@/modules/platformproduct/models/PlatformProduct';
import { FinancialAdvisorProBillingPlanForm } from '@/modules/financialadvisor/models/FinancialAdvisorProBillingPlanForm';
import { ProductKey } from '@/modules/estate/enums/ProductKey';
import { ModalOptions } from '@/modules/core/ui/services/ModalService';
import { FinancialAdvisorEvent } from '@/modules/financialadvisor/constants/FinancialAdvisorEvent';
import ConfirmModal from '@/modules/core/ui/components/ConfirmModal.vue';
import ModalService from '@/modules/core/ui/services/ModalService';
import Lang from '@/modules/core/app/services/Lang';
import BillingAdvisorProService from '@/modules/billing/services/BillingAdvisorProService';
import EventBus from '@/modules/core/app/helpers/EventBus';
import SnackbarService from '@/modules/core/ui/services/SnackbarService';

export class BillingAdvisorProChangePlanUIService extends BaseUIService {
  showUpgradeDowngradeModal(
    params: ModalOptions,
    plan: FinancialAdvisorProBillingPlanForm,
    platformProduct: PlatformProduct
  ): void {
    if (
      plan.productKey === ProductKey.ADVISOR_PRO_ANNUAL &&
      platformProduct.productKey === ProductKey.ADVISOR_PRO
    ) {
      this.showUpgradeModal(params, platformProduct, plan);
      return;
    }

    if (
      plan.productKey === ProductKey.ADVISOR_PRO &&
      platformProduct.productKey === ProductKey.ADVISOR_PRO_ANNUAL
    ) {
      this.showDowngradeModal(params, platformProduct, plan);
      return;
    }

    throw new Error(Lang.t('financialadvisor.invalid-upgrade-downgrade'));
  }

  showUpgradeModal(
    params: ModalOptions,
    platformProduct: PlatformProduct,
    plan: FinancialAdvisorProBillingPlanForm
  ): void {
    ModalService.show({
      parent: params.parent,
      component: ConfirmModal,
      props: {
        title: Lang.t('financialadvisor.upgrade-to-annual-plan'),
        subtitle: Lang.t('financialadvisor.upgrade-plan-subtitle', {
          endDate: platformProduct.isoFormattedNextBillingAt,
          price: plan.formattedPrice
        }),
        cancelButtonText: Lang.t('financialadvisor.no-go-back'),
        confirmButtonText: Lang.t('financialadvisor.yes-upgrade'),
        confirmFn: async () => {
          await this.onConfirmChangePlan(plan, 'financialadvisor.upgrade-to-annual-plan-success');
        }
      }
    });
  }

  showDowngradeModal(
    params: ModalOptions,
    platformProduct: PlatformProduct,
    plan: FinancialAdvisorProBillingPlanForm
  ): void {
    ModalService.show({
      parent: params.parent,
      component: ConfirmModal,
      props: {
        title: Lang.t('financialadvisor.to-monthly-plan'),
        subtitle: Lang.t('financialadvisor.downgrade-plan-subtitle', {
          endDate: platformProduct.isoFormattedNextBillingAt,
          price: plan.formattedPrice
        }),
        cancelButtonText: Lang.t('financialadvisor.no-go-back'),
        confirmButtonText: Lang.t('financialadvisor.yes-switch'),
        confirmFn: async () => {
          await this.onConfirmChangePlan(
            plan,
            'financialadvisor.downgrade-to-monthly-plan-success'
          );
        }
      }
    });
  }

  async onConfirmChangePlan(
    plan: FinancialAdvisorProBillingPlanForm,
    successMessageKey: string
  ): Promise<void> {
    const platformProduct: PlatformProduct = await BillingAdvisorProService.changePlan(
      plan.productKey!
    );
    const message: string = Lang.t(successMessageKey, {
      date: platformProduct.isoFormattedNextBillingAt
    });

    EventBus.signal(FinancialAdvisorEvent.REFRESH_BILLING_DASHBOARD);
    ModalService.closeAll();
    SnackbarService.showBottomBannerMessage(message, {
      indefinite: true,
      isCentered: true,
      icon: 'cls-check-circle-1'
    });
  }
}

export default new BillingAdvisorProChangePlanUIService();
